import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Compass, Award, FileCheck2, Building2, DollarSign } from "lucide-react";
import { SERVICES, SERVICE_CATEGORIES } from "../data/services";

const WV_SCENES = [
  {
    type: "video",
    videoId: "peFQQgTZM4M",
    alt: "Seneca Rocks, West Virginia — video by Brandon Gross",
    caption: "Seneca Rocks, West Virginia",
    credit: "Video © Brandon Gross. Used with permission.",
  },
  {
    type: "video",
    videoId: "r_lxZZa6GF4",
    alt: "West Virginia State Capitol video by Scott Abbott",
    caption: "The West Virginia State Capitol, Charleston, WV",
    credit: "Video © Scott Abbott. Used with permission.",
  },
  {
    type: "video",
    videoId: "v7Jw1zm1Dq0",
    alt: "Blackwater Falls drone video by PrettylilTraveler",
    caption: "Blackwater Falls, Davis, WV",
    credit: "Drone video © PrettylilTraveler. Used with permission.",
  },
  {
    type: "video",
    videoId: "ghfNXbt4IBE",
    alt: "New River Gorge Bridge drone video by Ben Childers",
    caption: "The Gauley Bridge over the New River, New River Gorge National Park, Fayette County, WV",
    credit: "Drone video © Ben Childers. Used with permission.",
  },
];

const buildEmbedSrc = (videoId) => {
  const origin =
    typeof window !== "undefined" && window.location && window.location.origin
      ? `&origin=${encodeURIComponent(window.location.origin)}`
      : "";
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1&enablejsapi=1${origin}`;
};

const STAT_BLOCKS = [
  {
    icon: DollarSign,
    overline: "Capital Raised",
    value: "$150M+",
    desc: "Federal, state, foundation, and private capital secured for our clients through grants, cooperative agreements, and contracts.",
  },
  {
    icon: Compass,
    overline: "Practice Areas",
    value: String(SERVICES.length),
    desc: "Specialized service lines spanning nonprofit, public sector, government contracting, and enterprise.",
  },
  {
    icon: FileCheck2,
    overline: "Standard",
    value: "2 CFR 200",
    desc: "Federal Uniform Guidance compliance built into every grants engagement.",
  },
  {
    icon: Building2,
    overline: "Sectors Served",
    value: "Nonprofit · Public · Civic",
    desc: "Boards, municipalities, housing authorities, schools, and community coalitions.",
  },
];

const Home = () => {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef(null);
  const ytReadyRef = useRef(false);
  const iframeIds = useRef(WV_SCENES.map((_, i) => `wv-yt-player-${i}`));

  // Load YouTube IFrame API once
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      ytReadyRef.current = true;
      return;
    }
    if (!document.getElementById("youtube-iframe-api-script")) {
      const tag = document.createElement("script");
      tag.id = "youtube-iframe-api-script";
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      ytReadyRef.current = true;
      if (typeof previous === "function") previous();
    };
  }, []);

  // Auto-rotate every 15s, but pause when the current video is playing
  useEffect(() => {
    if (isPlaying) return undefined;
    const id = setInterval(() => {
      setSceneIdx((i) => (i + 1) % WV_SCENES.length);
    }, 15000);
    return () => clearInterval(id);
  }, [isPlaying, sceneIdx]);

  // (Re)initialize YT.Player on scene change so we can listen for play/pause/end
  useEffect(() => {
    setIsPlaying(false);
    // Tear down previous player
    if (playerRef.current && typeof playerRef.current.destroy === "function") {
      try { playerRef.current.destroy(); } catch (e) { /* noop */ }
      playerRef.current = null;
    }
    const scene = WV_SCENES[sceneIdx];
    if (!scene || scene.type !== "video") return undefined;

    let cancelled = false;
    let attempts = 0;
    const tryInit = () => {
      if (cancelled) return;
      if (!ytReadyRef.current || !window.YT || !window.YT.Player) {
        attempts += 1;
        if (attempts > 50) return;
        setTimeout(tryInit, 200);
        return;
      }
      const elId = iframeIds.current[sceneIdx];
      const el = document.getElementById(elId);
      if (!el) {
        attempts += 1;
        if (attempts > 50) return;
        setTimeout(tryInit, 200);
        return;
      }
      try {
        playerRef.current = new window.YT.Player(elId, {
          events: {
            onStateChange: (e) => {
              // YT.PlayerState: PLAYING=1, PAUSED=2, ENDED=0, BUFFERING=3, CUED=5
              if (e.data === 1) {
                setIsPlaying(true);
              } else if (e.data === 0 || e.data === 2) {
                setIsPlaying(false);
              }
            },
          },
        });
      } catch (e) { /* noop */ }
    };
    const t = setTimeout(tryInit, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [sceneIdx]);

  const currentScene = WV_SCENES[sceneIdx];

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden bg-white"
        data-testid="home-hero"
      >
        <div className="absolute inset-0 nnc-grain opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-7 nnc-fade-up">
              <div className="flex items-center gap-3 mb-6">
                <span className="nnc-gold-rule" />
                <span className="nnc-overline" data-testid="hero-location">
                  Morgantown · West Virginia
                </span>
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight nnc-text leading-[1.05]"
                data-testid="hero-headline"
              >
                Nonprofit Navigation
                <br />
                <span className="relative">
                  Consulting, LLC.
                  <span className="block w-32 h-1 nnc-gold-bg mt-3" />
                </span>
              </h1>
              <p
                className="mt-8 text-lg leading-relaxed nnc-text max-w-xl"
                data-testid="hero-subheading"
              >
                We are a professional consulting firm that provides proposal
                writing, strategy, evaluation, and subject matter expertise
                to nonprofits, public agencies, government contracting,
                enterprises and community builders. We turn mission into
                structure, structure into strategy, and strategy into
                measurable impact—funded through grants, cooperative
                agreements, social impact funding, and government
                contracting alike.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/services"
                  className="nnc-btn"
                  data-testid="hero-cta-services"
                >
                  Explore Our Services <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/about"
                  className="nnc-btn-outline"
                  data-testid="hero-cta-about"
                >
                  About the Firm
                </Link>
              </div>
            </div>

            {/* Right: medallion */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end nnc-fade-up">
              <div className="relative">
                <div className="absolute -inset-6 nnc-ceramic-bg -z-10" style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" }} />
                <img
                  src="/assets/nnc-logo.svg"
                  alt="NNC Medallion — Guiding Mission-Driven Impact"
                  className="w-72 h-72 sm:w-80 sm:h-80 lg:w-[22rem] lg:h-[22rem] drop-shadow-xl"
                  data-testid="hero-medallion"
                />
              </div>
            </div>
          </div>
        </div>
        {/* Bottom gold rule */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] nnc-gold-bg" />
      </section>

      {/* OFFICIAL STATEMENT BAR */}
      <section
        className="nnc-bg text-white"
        data-testid="home-statement-bar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
          <div className="flex items-center gap-3">
            <Award className="h-6 w-6 nnc-gold-text" strokeWidth={2} />
            <span className="text-xs font-bold uppercase tracking-[0.28em] nnc-gold-text">
              Established Practice
            </span>
          </div>
          <p
            className="text-base lg:text-lg leading-relaxed text-white/95 max-w-4xl"
            data-testid="home-statement-text"
          >
            NNC partners with mission-driven organizations to design, fund,
            administer, and evaluate the work that strengthens communities —
            with the rigor that federal funders, nonprofit boards,
            contractors, and the public expect.
          </p>
        </div>
      </section>

      {/* STAT BLOCKS / FACTS */}
      <section className="bg-white" data-testid="home-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 nnc-stagger border border-[#E5E7EB]">
            {STAT_BLOCKS.map((b, i) => (
              <div
                key={b.overline}
                className={`p-8 lg:p-10 ${i !== STAT_BLOCKS.length - 1 ? "lg:border-r border-[#E5E7EB]" : ""}`}
                data-testid={`stat-block-${i}`}
              >
                <b.icon className="h-7 w-7 nnc-gold-text mb-5" strokeWidth={2} />
                <div className="nnc-overline mb-3">{b.overline}</div>
                <div className="text-3xl lg:text-4xl font-bold nnc-text leading-tight">
                  {b.value}
                </div>
                <p className="mt-4 text-sm leading-relaxed nnc-text/80" style={{ color: "#00205B" }}>
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES DIRECTORY PREVIEW */}
      <section
        className="nnc-ceramic-soft-bg border-t border-b border-[#E5E7EB]"
        data-testid="home-services-preview"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="nnc-gold-rule" />
                <span className="nnc-overline">Practice Directory</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight nnc-text">
                {SERVICES.length} practice areas. <br />One disciplined approach.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <p className="text-base leading-relaxed nnc-text max-w-md lg:ml-auto">
                Our services span foundational work for new nonprofits to
                advanced grants management for federally funded programs and
                public infrastructure projects.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 mt-6 text-xs font-bold uppercase tracking-widest nnc-text hover:nnc-gold-text transition-colors border-b-2 nnc-gold-border pb-1"
                data-testid="home-services-view-all"
              >
                View Full Directory <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>

          {/* Ledger preview - first 8 services */}
          <div className="bg-white border border-[#00205B]">
            {SERVICES.slice(0, 8).map((s) => (
              <Link
                to={`/services/${s.slug}`}
                key={s.slug}
                className="nnc-ledger-row flex items-stretch group"
                data-testid={`home-service-row-${s.slug}`}
              >
                <div className="w-20 lg:w-24 shrink-0 nnc-bg flex items-center justify-center">
                  <span className="text-white font-bold tracking-widest text-sm">
                    {s.number}
                  </span>
                </div>
                <div className="flex-1 px-6 py-5 lg:py-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                  <div className="md:col-span-4">
                    <div className="text-xs font-bold uppercase tracking-[0.18em] nnc-gold-text mb-1">
                      {s.category}
                    </div>
                    <div className="font-bold nnc-text text-base lg:text-lg leading-snug">
                      {s.title}
                    </div>
                  </div>
                  <div className="md:col-span-7 text-sm nnc-text/85" style={{ color: "#00205B" }}>
                    {s.summary}
                  </div>
                  <div className="md:col-span-1 md:text-right">
                    <ArrowRight className="h-5 w-5 inline-block nnc-text group-hover:nnc-gold-text transition-colors" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH / PILLARS */}
      <section className="bg-white" data-testid="home-approach">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-4">
                <span className="nnc-gold-rule" />
                <span className="nnc-overline">Method</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight nnc-text">
                How we work.
              </h2>
              <p className="mt-6 text-base leading-relaxed nnc-text">
                Every engagement follows a defined arc: diagnose, design,
                deliver, and document. The work is collaborative, transparent,
                and built to survive funder review and leadership transitions.
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E5E7EB]">
              {[
                {
                  n: "01",
                  t: "Diagnose",
                  d: "Listen first. Map the operating environment, the funding stack, and the real constraints.",
                },
                {
                  n: "02",
                  t: "Design",
                  d: "Build the plan with leadership and board. Pair vision with feasible budgets and timelines.",
                },
                {
                  n: "03",
                  t: "Deliver",
                  d: "Execute with discipline. Submit applications, stand up systems, and lead change.",
                },
                {
                  n: "04",
                  t: "Document",
                  d: "Leave behind clear records, dashboards, and SOPs that outlast the engagement.",
                },
              ].map((p) => (
                <div
                  key={p.n}
                  className="bg-white p-8 lg:p-10"
                  data-testid={`approach-pillar-${p.n}`}
                >
                  <div className="text-xs font-bold tracking-[0.25em] nnc-gold-text">
                    {p.n}
                  </div>
                  <div className="mt-3 text-xl font-bold nnc-text">{p.t}</div>
                  <p className="mt-3 text-sm leading-relaxed nnc-text">{p.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LOCATION CARD */}
      <section className="nnc-bg" data-testid="home-location-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="inline-block w-12 h-[2px] nnc-gold-bg" />
                <span className="text-xs font-bold uppercase tracking-[0.28em] nnc-gold-text">
                  Virtual Offices
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                Based in Morgantown,<br /> West Virginia.
              </h2>
              <p className="mt-6 text-base lg:text-lg leading-relaxed text-white/90 max-w-xl">
                Serving partners across our region, the country, and into
                Canada, we bring local knowledge of rural and urban realities
                together with a consulting practice that draws on a combined
                50 years of professional depth and national-grade significance.
              </p>
              <div className="mt-8 flex items-center gap-3 text-white">
                <MapPin className="h-5 w-5 nnc-gold-text" strokeWidth={2.5} />
                <span className="font-bold tracking-wide">
                  Morgantown, WV · United States
                </span>
              </div>
            </div>
            <div className="relative" data-testid="home-wv-slideshow">
              <div className="aspect-[4/3] w-full overflow-hidden border-4 nnc-gold-border bg-black relative">
                {WV_SCENES.map((s, i) => {
                  const isActive = i === sceneIdx;
                  if (s.type === "video") {
                    return (
                      <div
                        key={s.videoId}
                        className="absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out"
                        style={{ opacity: isActive ? 1 : 0, pointerEvents: isActive ? "auto" : "none" }}
                        data-testid={`home-wv-video-${i}`}
                      >
                        {isActive && (
                          <iframe
                            id={iframeIds.current[i]}
                            title={s.alt}
                            src={buildEmbedSrc(s.videoId)}
                            className="w-full h-full"
                            frameBorder="0"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        )}
                      </div>
                    );
                  }
                  return (
                    <img
                      key={s.videoId || s.src || i}
                      src={s.src}
                      alt={s.alt}
                      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
                      style={{ opacity: isActive ? 1 : 0 }}
                      data-testid={`home-wv-image-${i}`}
                    />
                  );
                })}
                {/* Caption banner overlaid bottom of image */}
                <div
                  className="absolute left-0 right-0 bottom-0 px-5 py-3 backdrop-blur-sm pointer-events-none"
                  style={{ backgroundColor: "rgba(0, 32, 91, 0.88)" }}
                  data-testid="home-wv-caption-banner"
                >
                  <p className="text-xs sm:text-sm font-bold tracking-wide text-white leading-snug">
                    {currentScene.caption}
                  </p>
                  {currentScene.credit && (
                    <p className="text-[10px] sm:text-[11px] mt-1 italic text-white/80 leading-snug">
                      {currentScene.credit}
                    </p>
                  )}
                </div>
              </div>
              {/* Slideshow indicator dots */}
              <div className="mt-4 flex items-center justify-center gap-2" data-testid="home-wv-dots">
                {WV_SCENES.map((s, i) => (
                  <button
                    key={s.videoId || s.src || i}
                    type="button"
                    onClick={() => setSceneIdx(i)}
                    aria-label={`Show scene ${i + 1}`}
                    data-testid={`home-wv-dot-${i}`}
                    className={`h-2 transition-all ${
                      i === sceneIdx
                        ? "w-8 nnc-gold-bg"
                        : "w-2 bg-[#E5E7EB] hover:bg-[#FCB814]"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-white border-t border-[#E5E7EB]" data-testid="home-cta-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="nnc-overline mb-3">Engage</div>
            <h3 className="text-2xl sm:text-3xl font-bold nnc-text tracking-tight max-w-2xl">
              Ready to advance your mission? Begin with a directory of our services.
            </h3>
          </div>
          <Link to="/services" className="nnc-btn whitespace-nowrap" data-testid="home-cta-bottom">
            View All Services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Home;
